/*=====================================================================*\
  Age gate: <wr-age-gate>

  Owns the age confirmation checkbox for one product form.

  - isSatisfied() → true when the box is ticked
  - Fills the "Age confirmed" and "_age_confirmed_at" line item properties
    while ticked, and clears them when unticked (plus native properties[...]
    mirrors used by express checkout, disabled while unticked)
  - Emits a bubbling `agegate:change` event on every state change
  - Shows / clears the inline error

  It NEVER touches the buy button. Button state has exactly one author:
  ProductForm.setSubmitState() in product.js, which listens for
  `agegate:change`. Do not add button writes here.
\*=====================================================================*/

if (!customElements.get('wr-age-gate')) {
  class WrAgeGate extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('[data-age-gate-checkbox]');
      // Each pair: the theme's line item property + a native properties[...] mirror for express checkout.
      this.valueInputs = this.querySelectorAll('[data-age-gate-value]');
      this.timestampInputs = this.querySelectorAll('[data-age-gate-timestamp]');
      this.errorEl = this.querySelector('[data-age-gate-error]');
      this.errorMessage = this.dataset.errorMessage || '';

      if (!this.checkbox) return;

      this.onChange = this.onChange.bind(this);
      this.checkbox.addEventListener('change', this.onChange);

      // Sync hidden inputs with the checkbox (e.g. a ticked box restored by bfcache).
      this.sync();
    }

    disconnectedCallback() {
      this.checkbox?.removeEventListener('change', this.onChange);
    }

    isSatisfied() {
      return Boolean(this.checkbox?.checked);
    }

    onChange() {
      this.sync();
      if (this.isSatisfied()) this.clearError();

      this.dispatchEvent(new CustomEvent('agegate:change', {
        bubbles: true,
        detail: { satisfied: this.isSatisfied() }
      }));
    }

    sync() {
      const satisfied = this.isSatisfied();

      // ISO 8601 UTC, e.g. 2026-10-08T14:03:21.512Z
      const timestamp = satisfied ? new Date().toISOString() : '';

      this.valueInputs.forEach((input) => {
        input.value = satisfied ? input.dataset.ageGateValue : '';
        if (input.name.startsWith('properties[')) input.disabled = !satisfied;
      });
      this.timestampInputs.forEach((input) => {
        input.value = timestamp;
        if (input.name.startsWith('properties[')) input.disabled = !satisfied;
      });

      // Native validity message, used if the form's reportValidity() reaches the box.
      this.checkbox.setCustomValidity(satisfied ? '' : this.errorMessage);
    }

    /**
     * Called by ProductForm when a submit is attempted while unconfirmed.
     */
    showError() {
      if (this.errorEl) this.errorEl.textContent = this.errorMessage;
      this.checkbox?.setAttribute('aria-invalid', 'true');
      this.classList.add('has-error');
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.checkbox?.focus({ preventScroll: true });
    }

    clearError() {
      if (this.errorEl) this.errorEl.textContent = '';
      this.checkbox?.removeAttribute('aria-invalid');
      this.classList.remove('has-error');
    }
  }

  customElements.define('wr-age-gate', WrAgeGate);
}
